import React, { useEffect, useState } from "react";
import Search from "components/Search/Search";
import { useNavigate, useLocation } from "react-router-dom";
import { buildQueryString } from "utils/queryStrings";
import {
  searchFormSelector,
  changeSearchFormMetaStorageTimeout,
} from "redux/searchForm";
import { useDispatch, useSelector } from "react-redux";
import { useTranslations } from "hooks/useTranslations";
import { CustomAnimation, MediaQuery } from "@thekilroygroup/shared-ui";
import {
  backgroundAnimations,
  navigateDelay,
  searchFormAnimations,
} from "./animations.frontPage";
import { subtitleAnimations, titleAnimations } from "utils/sharedAnimations";
import { Typography, Paper, Container, Stack, SxProps } from "@mui/material";
import { changeSearchQueryMetaStorageTimeout } from "redux/searchQuery";
import { LanguageSelector } from "../../components/LanguageSelector/LanguageSelector";

const HomePage = () => {
  const { translations } = useTranslations();
  const translationsFrontPage = translations.frontPage;
  const searchForm = useSelector(searchFormSelector);
  const navigate = useNavigate();
  const location = useLocation();
  const [isIn, setIsIn] = useState(true);
  const dispatch = useDispatch();

  useEffect(() => {
    // Remove Google linker param `_gl` from URL on FrontPage only
    const params = new URLSearchParams(location.search);
    if (params.has("_gl")) {
      params.delete("_gl");
      navigate(
        {
          pathname: location.pathname,
          search: params.toString(),
          hash: location.hash,
        },
        { replace: true },
      );
    }
  }, [location, navigate]);

  useEffect(() => {
    dispatch(changeSearchFormMetaStorageTimeout(new Date(0)));
    dispatch(changeSearchQueryMetaStorageTimeout(new Date(0)));
  }, []);

  const navigateToSearchResults = () => {
    setIsIn(false);
    setTimeout(() => {
      navigate(`/search-results?${buildQueryString(searchForm)}`);
    }, navigateDelay);
  };

  const navigateToLowpriceCalendar = () => {
    setIsIn(false);
    setTimeout(() => {
      navigate(`/calendar?${buildQueryString(searchForm)}`);
    }, navigateDelay);
  };

  const { wrapper, titleWrapper, title, paper } = sxStyles;
  return (
    <>
      <CustomAnimation {...backgroundAnimations} in={isIn}>
        <LanguageSelector
          color="white"
          sxProps={{ position: "absolute", top: 0, right: 8, zIndex: 100 }}
        />
        <Stack
          sx={{
            ...wrapper,
          }}
        >
          <img
            src={`${translationsFrontPage.bgImage.src}`}
            alt="Background image of clouds"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              position: "absolute",
              zIndex: -1,
            }}
          />
          <Stack sx={titleWrapper}>
            <CustomAnimation {...titleAnimations} in={isIn}>
              <Typography variant="h1" sx={title}>
                {translationsFrontPage.title}
              </Typography>
            </CustomAnimation>
            <CustomAnimation {...subtitleAnimations} in={isIn}>
              <Typography
                variant="h5"
                component={"h2"}
                sx={{
                  color: "white.main",
                }}
              >
                {translationsFrontPage.subtitle}
              </Typography>
            </CustomAnimation>
          </Stack>
          <MediaQuery max="md">
            <CustomAnimation in={isIn} {...subtitleAnimations}>
              <Paper variant="elevation" elevation={15} sx={paper}>
                <Search
                  handleSubmit={() => navigateToSearchResults()}
                  navigateToLowpriceCalendar={navigateToLowpriceCalendar}
                />
              </Paper>
            </CustomAnimation>
          </MediaQuery>
          <MediaQuery min="md">
            <Container maxWidth="lg">
              <CustomAnimation in={isIn} {...searchFormAnimations}>
                <Paper variant="elevation" elevation={16} sx={paper}>
                  <Search handleSubmit={() => navigateToSearchResults()} />
                </Paper>
              </CustomAnimation>
            </Container>
          </MediaQuery>
        </Stack>
      </CustomAnimation>
    </>
  );
};

export default HomePage;

const sxStyles: { [key: string]: SxProps } = {
  wrapper: {
    width: "100vw",
    minHeight: "100vh",
    backgroundSize: "cover",
    backgroundPosition: "center",
    justifyContent: { xs: "flex-end", md: "center" },
    alignItems: { md: "center" },
    position: "relative",
  },
  title: {
    color: "white.main",
    my: { md: 2 },
  },
  titleWrapper: {
    p: { xs: 2, sm: 3, md: 4 },
    mt: "10vh",
    justifyContent: { md: "space-around" },
    alignItems: { md: "center" },
  },
  paper: {
    py: { xs: 2, md: 3 },
    px: { md: 1 },
    borderRadius: { md: 2 },
    mb: "14vh",
  },
};
